import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { formatApiErrorDetail } from "./AuthPage";

const inputCls = "w-full bg-white border-2 border-black rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-[#FFE066]/50 text-base";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (password.length < 6) { setError("Password needs at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="w-full max-w-md space-y-5">
        <img src="/mascot.png" alt="StudySpark mascot" className="w-20 h-20 mx-auto rounded-full border-4 border-black shadow-[4px_4px_0px_rgba(0,0,0,1)] bg-white object-cover" />
        <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6 space-y-4" data-testid="reset-password-card">
          {done ? (
            <div className="text-center space-y-3">
              <CheckCircle2 size={36} className="mx-auto text-[#51CF66]" />
              <h1 className="font-heading text-2xl font-semibold">Password updated!</h1>
              <p className="text-sm text-gray-600">You can log in with your new password now.</p>
              <Link to="/auth" data-testid="reset-login-link"
                className="inline-block bg-[#4D96FF] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] font-bold px-6 py-3">
                Go to log in
              </Link>
            </div>
          ) : !token ? (
            <div className="text-center space-y-3">
              <h1 className="font-heading text-2xl font-semibold">Invalid link</h1>
              <p className="text-sm text-gray-600">This reset link is missing its token. Request a new one.</p>
              <Link to="/forgot-password" className="font-bold underline text-sm">Request new link</Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-semibold flex items-center gap-2"><KeyRound size={22} /> Set a new password</h1>
              <input type="password" className={inputCls} placeholder="New password (min 6 characters)"
                value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" />
              <input type="password" className={inputCls} placeholder="Confirm new password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="reset-confirm-input" />
              {error && <p className="text-sm font-bold text-[#FF6B6B]" data-testid="reset-error">{error}</p>}
              <button onClick={submit} disabled={busy} data-testid="reset-submit-button"
                className="w-full bg-[#4D96FF] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold py-3 disabled:opacity-60 flex items-center justify-center gap-2">
                {busy && <Loader2 size={18} className="animate-spin" />} Update password
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
